<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import {
  isOnMentionsView,
  isOnUnattendedView,
  isOnFoldersView,
} from 'dashboard/store/modules/conversations/helpers/actionHelpers';
import ConversationCard from 'dashboard/components/widgets/conversation/ConversationCard.vue';
import ContactMessageTimeline from './ContactMessageTimeline.vue';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import ConversationContextMenu from 'dashboard/components/widgets/conversation/contextMenu/Index.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  contactId: { type: [String, Number], required: true },
  conversationId: { type: [String, Number], required: true },
});

const store = useStore();
const route = useRoute();
const router = useRouter();

const currentChat = useMapGetter('getSelectedChat');
const uiFlags = useMapGetter('contactConversations/getUIFlags');
const hasMoreGetter = useMapGetter('contactConversations/getHasMore');
const hasMore = computed(() => hasMoreGetter.value(props.contactId));

const contactGetter = useMapGetter('contacts/getContact');
const inboxGetter = useMapGetter('inboxes/getInbox');

const inboxesList = useMapGetter('inboxes/getInboxes');
const showInboxName = computed(() => inboxesList.value.length > 1);

const contactConversationGetter = useMapGetter(
  'contactConversations/getContactConversation'
);
const conversations = computed(() =>
  contactConversationGetter.value(props.contactId)
);

const previousConversations = computed(() =>
  conversations.value.filter(c => c.id !== Number(props.conversationId))
);

const activeContextChat = ref(null);
const showContextMenu = ref(false);
const contextMenu = ref({ x: null, y: null });

const buildConversationUrl = conversation => {
  const {
    params: { accountId, inbox_id: inboxId, label, teamId },
    name,
  } = route;

  let conversationType = '';
  if (isOnMentionsView({ route: { name } })) {
    conversationType = 'mention';
  } else if (isOnUnattendedView({ route: { name } })) {
    conversationType = 'unattended';
  }

  return frontendURL(
    conversationUrl({
      accountId,
      activeInbox: inboxId ? conversation.inbox_id || inboxId : inboxId,
      id: conversation.id,
      label,
      teamId,
      foldersId: isOnFoldersView({ route: { name } }) ? route.params.id : 0,
      conversationType,
    })
  );
};

const conversationPath = computed(() => {
  if (!activeContextChat.value) return '';
  return buildConversationUrl(activeContextChat.value);
});

const onCardClick = async (conversation, e, messageId = null) => {
  const path = messageId
    ? frontendURL(
        conversationUrl({
          accountId: route.params.accountId,
          id: conversation.id,
        })
      )
    : buildConversationUrl(conversation);
  if (!path) return;

  const target = messageId ? { path, query: { messageId } } : { path };

  if (e.metaKey || e.ctrlKey) {
    e.preventDefault();
    window.open(
      `${window.chatwootConfig.hostURL}${path}${messageId ? `?messageId=${messageId}` : ''}`,
      '_blank',
      'noopener,noreferrer'
    );
    return;
  }

  await router.push(target);
  if (messageId && currentChat.value.id === conversation.id) {
    if (
      !currentChat.value.messages?.some(message => message.id === messageId)
    ) {
      await store.dispatch('fetchPreviousMessages', {
        conversationId: conversation.id,
        after: messageId,
        before: currentChat.value.messages?.[0]?.id,
      });
    }
    if (currentChat.value.id === conversation.id) {
      emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
    }
  }
};

const openContextMenu = (conversation, e) => {
  e.preventDefault();
  activeContextChat.value = conversation;
  contextMenu.value.x = e.pageX || e.clientX;
  contextMenu.value.y = e.pageY || e.clientY;
  showContextMenu.value = true;
};

const closeContextMenu = () => {
  showContextMenu.value = false;
  contextMenu.value.x = null;
  contextMenu.value.y = null;
  activeContextChat.value = null;
};

const loadMore = () => {
  store.dispatch('contactConversations/loadMore', props.contactId);
};

watch(
  () => props.contactId,
  (newId, oldId) => {
    if (newId && newId !== oldId) {
      showContextMenu.value = false;
      activeContextChat.value = null;
      store.dispatch('contactConversations/get', newId);
    }
  }
);

onMounted(() => {
  store.dispatch('contactConversations/get', props.contactId);
});
</script>

<template>
  <div v-if="!uiFlags.isFetching" class="">
    <div v-if="!previousConversations.length" class="no-label-message px-4 p-3">
      <span>
        {{ $t('CONTACT_PANEL.CONVERSATIONS.NO_RECORDS_FOUND') }}
      </span>
    </div>
    <div v-else class="min-w-0">
      <ContactMessageTimeline :contact-id="contactId" @open="onCardClick" />
      <details class="border-t border-n-weak">
        <summary class="cursor-pointer px-4 py-2 text-sm text-n-slate-11">
          {{ $t('CONTACT_PANEL.CONVERSATIONS.TITLE') }}
        </summary>
        <div
          class="contact-conversation--list [&>.conversation:last-child]:!border-b-0 [&>.conversation:last-child:hover]:!border-b-0 [&>.conversation:last-child]:!rounded-b-lg"
        >
          <ConversationCard
            v-for="conversation in previousConversations"
            :key="conversation.id"
            :chat="conversation"
            :current-contact="
              contactGetter(conversation.meta?.sender?.id) || {}
            "
            :assignee="conversation.meta?.assignee || {}"
            :inbox="inboxGetter(conversation.inbox_id) || {}"
            :is-active-chat="currentChat.id === conversation.id"
            show-assignee
            :show-inbox-name="showInboxName"
            show-last-message-sender
            hide-thumbnail
            compact
            @click="onCardClick(conversation, $event)"
            @contextmenu="openContextMenu(conversation, $event)"
          />
        </div>
        <div v-if="hasMore" class="flex justify-center px-4 py-2">
          <Button
            variant="ghost"
            size="sm"
            :is-loading="uiFlags.isLoadingMore"
            :disabled="uiFlags.isLoadingMore"
            :label="$t('CONTACTS_LAYOUT.LOAD_MORE')"
            @click="loadMore"
          />
        </div>
      </details>
    </div>
    <ContextMenu
      v-if="showContextMenu && activeContextChat"
      :x="contextMenu.x"
      :y="contextMenu.y"
      @close="closeContextMenu"
    >
      <ConversationContextMenu
        :status="activeContextChat.status"
        :inbox-id="activeContextChat.inbox_id"
        :priority="activeContextChat.priority"
        :chat-id="activeContextChat.id"
        :has-unread-messages="activeContextChat.unread_count > 0"
        :conversation-labels="activeContextChat.labels"
        :conversation-url="conversationPath"
        :allowed-options="['open-new-tab', 'copy-link']"
        @close="closeContextMenu"
      />
    </ContextMenu>
  </div>
  <div v-else class="flex items-center justify-center py-5">
    <Spinner />
  </div>
</template>

<style lang="scss" scoped>
.no-label-message {
  @apply text-n-slate-11 mb-4;
}
</style>
